import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaArrowLeft, FaRegClock, FaRegNewspaper, FaUser } from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./footer";
import { Button, Card, Container, IconTile } from "./ui";
import { fetchBlog, formatBlogDate } from "../../services/blogs";

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  // The second call: only this post's data, fetched by the id in the URL.
  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    window.scrollTo({ top: 0 });

    fetchBlog(id)
      .then((row) => {
        if (cancelled) return;
        if (!row) {
          setStatus("error");
          return;
        }
        setBlog(row);
        setStatus("ready");
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(`Error fetching blog ${id}:`, error);
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="flex flex-col">
      <Navbar />

      <section className="bg-white py-16">
        <Container>
          <Link
            to="/blogs"
            className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-darkgrey transition-colors duration-300 hover:text-brand"
          >
            <FaArrowLeft className="h-3 w-3" />
            Back to Blogs
          </Link>

          {status === "loading" && (
            <div className="mx-auto max-w-3xl space-y-5">
              <div className="h-72 w-full animate-pulse rounded-3xl bg-brand-50" />
              <div className="h-8 w-3/4 animate-pulse rounded bg-brand-50" />
              <div className="h-4 w-full animate-pulse rounded bg-brand-50" />
              <div className="h-4 w-5/6 animate-pulse rounded bg-brand-50" />
            </div>
          )}

          {status === "error" && (
            <Card className="mx-auto flex max-w-xl flex-col items-center gap-4 p-12 text-center">
              <IconTile icon={FaRegNewspaper} size="lg" />
              <div>
                <h1 className="text-base font-bold text-ink">
                  This post couldn't be loaded
                </h1>
                <p className="mt-1 text-sm text-darkgrey">
                  It may have been removed, or the service is unavailable.
                </p>
              </div>
              <Button as={Link} to="/blogs">
                Browse all blogs
              </Button>
            </Card>
          )}

          {status === "ready" && blog && (
            <article className="mx-auto max-w-3xl">
              {blog.image && (
                <div className="mb-8 aspect-[16/7] overflow-hidden rounded-3xl bg-brand-50">
                  <img
                    src={blog.image}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              <h1 className="text-3xl font-bold leading-tight text-ink sm:text-4xl">
                {blog.title}
              </h1>

              {/* Byline */}
              <div className="mt-4 flex flex-wrap items-center gap-5 border-b border-brand-100 pb-6">
                {formatBlogDate(blog.date) && (
                  <span className="flex items-center gap-2 text-sm text-darkgrey">
                    <FaRegClock className="h-3.5 w-3.5 text-brand" />
                    {formatBlogDate(blog.date)}
                  </span>
                )}
                {blog.author && (
                  <span className="flex items-center gap-2 text-sm text-darkgrey">
                    <FaUser className="h-3.5 w-3.5 text-brand" />
                    {blog.author}
                  </span>
                )}
              </div>

              {/* Body — reuses the rich-text styling the policy pages already use */}
              <div
                className="policies-content mt-8 text-sm font-medium leading-relaxed text-darkgrey"
                dangerouslySetInnerHTML={{ __html: blog.body }}
              />
            </article>
          )}
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default BlogDetail;
